import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  useTheme
} from '@mui/material';
import { Star, Refresh } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { fetchWatchlist } from '../marketsSlice';
import apiClient from '../../../api/apiClient';
import { formattedSymbol, itemVariants } from './utils';

/**
 * WatchlistWidget - Displays user's watchlist with prices
 * 
 * @param {Object} config - Widget configuration
 * @param {string} [config.title] - Custom title (default: "My Watchlist")
 * @param {number|string} [config.maxHeight] - Maximum height for list
 * @param {boolean} [config.showRefresh] - Whether to show refresh button (default: true)
 */
const WatchlistWidget = ({ config = {} }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { watchlist, watchlistLoading } = useSelector((state) => state.markets);
  
  const [watchlistPrices, setWatchlistPrices] = useState({});
  const [watchlistPricesLoading, setWatchlistPricesLoading] = useState(false);
  
  const title = config.title || 'My Watchlist';
  const maxHeight = config.maxHeight || '300px';
  const showRefresh = config.showRefresh !== false;

  // Fetch watchlist on mount
  useEffect(() => {
    dispatch(fetchWatchlist());
  }, [dispatch]);

  // Fetch prices for watchlist symbols
  useEffect(() => {
    let isCancelled = false;
    
    const fetchWatchlistPrices = async () => {
      if (watchlist.length === 0) {return;}
      
      setWatchlistPricesLoading(true);
      try {
        const pricePromises = watchlist.map(async (symbol) => {
          try {
            const normalizedSymbol = formattedSymbol(symbol);
            const response = await apiClient.get(`/market-data/${normalizedSymbol}?timeframe=daily&limit=2`);
            const candles = response.data.candles || [];
            if (candles.length >= 1) {
              const latest = candles[candles.length - 1];
              const previous = candles.length >= 2 ? candles[candles.length - 2] : latest;
              const latestClose = latest.close ?? latest.c ?? latest.Close ?? latest.closePrice ?? latest.close_price;
              const prevClose = previous.close ?? previous.c ?? previous.Close ?? previous.closePrice ?? previous.close_price;
              // eslint-disable-next-line eqeqeq
              const changePercent = (prevClose != null && prevClose !== 0) ? ((latestClose - prevClose) / prevClose * 100) : null;
              return { symbol, price: latestClose, changePercent };
            }
            return { symbol, price: null, changePercent: null };
          } catch (err) {
            return { symbol, price: null, changePercent: null };
          }
        });
        
        const results = await Promise.all(pricePromises);
        const priceMap = {};
        results.forEach(r => { priceMap[r.symbol] = { price: r.price, changePercent: r.changePercent }; });
        if (!isCancelled) {
          setWatchlistPrices(priceMap);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch watchlist prices:', err);
      } finally {
        if (!isCancelled) {
          setWatchlistPricesLoading(false);
        }
      }
    };
    
    fetchWatchlistPrices();
    
    return () => { isCancelled = true; };
  }, [watchlist]);

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Star color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({watchlist.length} items)
              </Typography>
            </Box>
            {showRefresh && (
              <Tooltip title="Refresh Watchlist">
                <IconButton 
                  size="small" 
                  onClick={() => dispatch(fetchWatchlist({ force: true }))}
                  disabled={watchlistLoading}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Content */}
          {watchlistLoading ? (
            <Box flexGrow={1} overflow="hidden">
              <List sx={{ p: 0, maxHeight, overflow: 'auto' }}>
                {[1, 2, 3, 4].map((i) => (
                  <React.Fragment key={i}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Skeleton variant="circular" width={20} height={20} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Skeleton variant="text" width="60%" />}
                        secondary={<Skeleton variant="text" width="40%" />}
                      />
                    </ListItem>
                    {i < 4 && <Divider variant="inset" component="li" sx={{ ml: 5 }} />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ) : watchlist.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              flexGrow={1}
              gap={2}
            >
              <Star sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No watchlist items found
              </Typography>
              <Typography variant="caption" color="text.disabled" textAlign="center">
                Add symbols to your watchlist to see them here
              </Typography>
            </Box>
          ) : (
            <Box flexGrow={1} overflow="hidden">
              <List sx={{ p: 0, maxHeight, overflow: 'auto' }}>
                {watchlist.map((symbol, index) => (
                  <React.Fragment key={symbol}>
                    <ListItem 
                      component={Link}
                      to={`/markets/${formattedSymbol(symbol)}`}
                      sx={{ 
                        px: 0, 
                        py: 1,
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: (theme.vars || theme).palette.action.hover,
                          borderRadius: 1
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Star color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {symbol}
                          </Typography>
                        }
                        secondary={
                          watchlistPricesLoading ? (
                            <Skeleton variant="text" width={80} />
                          // eslint-disable-next-line eqeqeq
                          ) : watchlistPrices[symbol]?.price != null ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="caption" fontWeight={600}>
                                ${watchlistPrices[symbol].price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                              {/* eslint-disable-next-line eqeqeq */}
                              {watchlistPrices[symbol].changePercent != null && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: watchlistPrices[symbol].changePercent >= 0 ? '#22c55e' : '#ef4444',
                                    fontWeight: 600
                                  }}
                                >
                                  {watchlistPrices[symbol].changePercent >= 0 ? '+' : ''}{watchlistPrices[symbol].changePercent.toFixed(2)}%
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Click to view market details
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                    {index < watchlist.length - 1 && (
                      <Divider variant="inset" component="li" sx={{ ml: 5 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WatchlistWidget;
